import { SECRET_API } from '@/common/constants/constants';
import { useCSRFToken } from '@/pages/contextProvider';
import { HEADER_NAME } from '@eventbrite/csrf-double-cookie';
import { AlertOutlineIcon, SuccessIcon, Typography } from '@eventbrite/marmalade';
import React, { useEffect, useState } from 'react';
import styles from './Captcha.module.scss';

interface AwsWafCaptchaType {
  renderCaptcha: (
    container: HTMLElement | null,
    options: {
      apiKey: string;
      onSuccess?: (wafToken: any) => void;
      onError?: (error: Error) => void;
    },
  ) => void;
}

declare const AwsWafCaptcha: AwsWafCaptchaType;

interface CaptchaKeys {
  CAPTCHA_API_KEY: string;
  CAPTCHA_SCRIPT_SRC: string;
  SKIP_CAPTCHA?: boolean;
}

const fetchCaptchaKeys = async (csrfToken: string): Promise<CaptchaKeys> => {
  const response = await fetch(SECRET_API, {
    headers: {
      'Content-Type': 'application/json',
      [HEADER_NAME]: csrfToken,
    },
  });
  if (!response.ok) {
    throw new Error('Failed to load Captcha keys');
  }
  const data = await response.json();
  return data.keys;
};

const loadCaptchaScript = (
  apiKey: string,
  scriptSrc: string,
  onSuccess: (wafToken: any) => void,
  onError: (error: Error) => void,
): (() => void) => {
  const script = document.createElement('script');
  script.src = scriptSrc;
  script.async = true;
  script.defer = true;

  script.onload = () => {
    const container = document.getElementById('captcha-container');
    if (container) {
      AwsWafCaptcha.renderCaptcha(container, { apiKey, onSuccess, onError });
    }
  };
  document.body.appendChild(script);

  return () => {
    document.body.removeChild(script);
  };
};

const Captcha: React.FC<{
  setIsCaptchaSolved: (isSolved: boolean) => void;
  validatedCaptcha?: boolean;
}> = ({ setIsCaptchaSolved, validatedCaptcha }) => {
  const csrfToken = useCSRFToken();
  const [error, setError] = useState<string | null>(null);
  const [isCaptchaSolved, setIsCaptchaSolvedState] = useState<boolean>(false);
  const [captchaKeys, setCaptchaKeys] = useState<CaptchaKeys | null>(null);

  useEffect(() => {
    const initializeCaptchaKeys = async (csrfToken: string) => {
      try {
        const keys = await fetchCaptchaKeys(csrfToken);
        // Playwright E2E tests intercept the `/captcha-secrets` request and
        // return the `SKIP_CAPTCHA` flag as true.
        // This path marks the captcha as solved
        if ((keys as any).SKIP_CAPTCHA) {
          setIsCaptchaSolvedState(true);
          setIsCaptchaSolved(true);
          return;
        }

        setCaptchaKeys(keys);
      } catch (error) {
        setError('Error Fetching Captcha Keys');
      }
    };

    initializeCaptchaKeys(csrfToken);
  }, []);

  useEffect(() => {
    if (!captchaKeys || captchaKeys.SKIP_CAPTCHA) return;

    const onSuccess = (wafToken: any) => {
      setIsCaptchaSolvedState(true);
      setIsCaptchaSolved(true);
      const captchaContainer = document.getElementById('captcha-container');
      if (captchaContainer) {
        captchaContainer.style.display = 'none';
      }
    };
    const onError = (error: Error) => setError(error.message);

    const cleanUpScript = loadCaptchaScript(
      captchaKeys.CAPTCHA_API_KEY,
      captchaKeys.CAPTCHA_SCRIPT_SRC,
      onSuccess,
      onError,
    );

    return cleanUpScript;
  }, [captchaKeys, setIsCaptchaSolved]);

  return (
    <div className={styles.captcha_container}>
      <div
        data-testid="captcha-container"
        id="captcha-container"
        style={{ margin: '1em auto' }}
      ></div>
      {isCaptchaSolved && (
        <div className={styles.captcha_success_container}>
          <SuccessIcon size="small" className={styles.success_icon} />
          <Typography variant="body-md" className={styles.text}>
            Verified successfully. You can continue submitting the form.
          </Typography>
        </div>
      )}
      {error && (
        <div data-testid="captcha-error" style={{ color: 'red' }}>
          {error}
        </div>
      )}
      {!validatedCaptcha && !isCaptchaSolved && (
        <div className={styles.captcha_error_container}>
          <AlertOutlineIcon size="small" className={styles.error_icon} />
          <Typography variant="body-md" className={styles.text}>
            Please solve the captcha before submitting the form.
          </Typography>
        </div>
      )}
    </div>
  );
};

export default Captcha;
